/**
 * Contracten van de Zoea Sites-bouwer.
 *
 * Deze types spiegelen `backend/websites/builder_schemas.py` veld-voor-veld.
 * De backend is en blijft de autoriteit: elke regel die hier staat (slugvorm,
 * uniciteit, één homepage) wordt daar opnieuw afgedwongen. Wat hier leeft is
 * er zodat de gebruiker het antwoord ziet terwijl hij typt -- niet in plaats
 * van de servercontrole.
 */

export type BlockType = 'heading' | 'paragraph' | 'rich_text' | 'button' | 'divider' | 'spacer' | 'image' | 'shape' | 'video' | 'cards' | 'contact' | 'form' | 'badge' | 'feature_list' | 'quote' | 'faq' | 'gallery' | 'custom_html';

export interface SiteMedia {
  id: string;
  website_id: string;
  organization_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
  alt_text: string;
  focal_x: number;
  focal_y: number;
  source_url?: string | null;
  created_at: string;
}

/** The canonical editing target when the Media Library is opened in context. */
export type MediaTarget =
  | { kind: 'block'; blockId: string }
  | { kind: 'section-background'; sectionId: string }
  | { kind: 'header-logo' }
  | { kind: 'footer-logo'; elementId: string; index: number }
  | { kind: 'favicon' }
  | { kind: 'default-og-image' };

export type V2BlockType = BlockType | 'rich_text' | 'button' | 'divider' | 'spacer' | 'image' | 'cards' | 'contact';

export interface SiteColumn {
  columnId: string;
  blocks: ContentBlock[];
}

export interface SiteSection {
  sectionId: string;
  layout: 'full' | 'content';
  columns: SiteColumn[];
  background?: string;
  spacing?: 'sm' | 'md' | 'lg';
  anchor?: string;
  columnLayout?: ColumnLayout;
  style?: SectionStyle;
  presetId?: string | null;
}

export interface SectionStyle {
  widthMode?: 'content' | 'wide' | 'full';
  spacing?: 'compact' | 'normal' | 'spacious' | 'extra';
  /** Vertical gap at the top edge of this section. */
  topSpacing?: 'flush' | 'compact' | 'normal';
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'center' | 'bottom';
  minHeight?: 'auto' | 'medium' | 'tall' | 'screen';
  /** Canonical freeform section geometry. Narrow viewports override desktop. */
  height?: ResponsiveSectionHeight;
  colorScheme?: 'light' | 'dark';
  textColor?: string | null;
  background?: { mode?: 'none' | 'solid' | 'gradient' | 'image'; color?: ColorRole; customColor?: string | null; gradientFrom?: ColorRole; gradientFromCustom?: string | null; gradientTo?: ColorRole; gradientToCustom?: string | null; gradientDirection?: 'to-bottom' | 'to-right' | 'to-bottom-right' | 'radial'; imageAssetId?: string | null; focalX?: number; focalY?: number; overlay?: ColorRole; overlayCustom?: string | null; overlayIntensity?: number; legacyColor?: string | null };
  topDivider?: { style?: string; size?: 'sm' | 'md' | 'lg'; flip?: boolean; color?: ColorRole; customColor?: string | null };
  bottomDivider?: { style?: string; size?: 'sm' | 'md' | 'lg'; flip?: boolean; color?: ColorRole; customColor?: string | null };
  decoration?: { style?: string; intensity?: 'subtle' | 'medium' | 'strong'; position?: string; color?: ColorRole; customColor?: string | null };
  motion?: 'none' | 'fade' | 'fade-up' | 'scale';
}
/**
 * Mirrors `design_schemas.ColumnLayout`. Proportional naming: the digits are
 * relative widths, so '1-2' is one part beside two and '1-3' is a quarter
 * beside three quarters. Mirrored pairs are distinct layouts, not a reverse
 * flag, because mobile stacking follows source order.
 */
export type ColumnLayout = '1' | '2' | '1-2' | '2-1' | '1-3' | '3-1' | '3' | '4' | '1-1-2' | '2-1-1';

/** Mirrors `design_schemas.COLUMN_COUNTS`. */
export const COLUMN_COUNTS: Record<ColumnLayout, number> = {
  '1': 1, '2': 2, '1-2': 2, '2-1': 2, '1-3': 2, '3-1': 2, '3': 3, '4': 4, '1-1-2': 3, '2-1-1': 3,
};

export type ColorRole = 'primary' | 'secondary' | 'accent' | 'background' | 'surface' | 'text' | 'muted' | 'dark' | 'light';

/** Mirrors `design_schemas.FooterElement`. One shape with a `type`
 * discriminator so switching an element's type in the Builder keeps whatever
 * was already typed. */
export interface FooterElement {
  elementId: string;
  type:
    | 'logo' | 'text' | 'links' | 'contact' | 'button' | 'socials'
    | 'partner_logos' | 'legal' | 'copyright' | 'cookie_preferences' | 'spacer' | 'divider' | 'shape';
  heading: string;
  align?: 'left' | 'center' | 'right';
  fontSize?: number;
  text: string;
  links: Array<{ label: string; pageId?: string | null; href?: string | null }>;
  socials: Array<{ platform: string; href: string }>;
  logos: Array<{ assetId?: string | null; name: string; href?: string | null }>;
  address: string;
  email: string;
  phone: string;
  buttonLabel: string;
  destination?: ButtonDestination | null;
  button?: ButtonPresentation | null;
  logoAssetId?: string | null;
  flow?: 'stack' | 'row' | 'none';
  gap?: number;
  padding?: number;
  shape?: VisualSurface | null;
  /** Optional direct-manipulation placement, shared with page elements. */
  layout?: ElementLayout;
  /** Explicit absolute footer geometry. When present this is authoritative;
   * `layout` remains the legacy grid contract and is never merged into it at
   * render time. */
  freeformLayout?: FreeformLayout;
}

export interface FooterColumn {
  columnId: string;
  elements: FooterElement[];
}

export interface SiteFooter {
  layout: 'simple' | 'columns';
  positioning?: 'flow' | 'freeform';
  connectToLastSection?: boolean;
  surface: 'light' | 'dark' | 'theme';
  backgroundColor?: string | null;
  textColor?: string | null;
  descriptionFontSize?: number;
  smallTextFontSize?: number;
  descriptionAlign?: 'left' | 'center' | 'right';
  smallTextAlign?: 'left' | 'center' | 'right';
  height?: number;
  minHeight?: number;
  description: string;
  showNavigation: boolean;
  showSiteName?: boolean;
  address: string;
  email: string;
  phone: string;
  socials: Array<{ platform: string; href: string }>;
  smallText: string;
  showSmallText?: boolean;
  /** The composable model. The flat fields above are retained because stored
   * websites have them; the Platform migrates them onto columns on read. */
  columnLayout: '1' | '2' | '3' | '4';
  columns: FooterColumn[];
}

export interface SiteChrome {
  theme: { colors: Record<Exclude<ColorRole, 'light'>, string>; typography: { pairing: 'modern' | 'humanist' | 'editorial' | 'classic'; headingScale: 'sm' | 'md' | 'lg'; bodyScale: 'sm' | 'md' | 'lg' }; buttons: { treatment: 'filled' | 'outline'; radius: 'sharp' | 'subtle' | 'rounded' | 'soft'; size: 'normal' | 'large' }; shapes: { radius: 'sharp' | 'subtle' | 'rounded' | 'soft'; shadow: 'none' | 'subtle' | 'elevated'; imageRadius: 'sharp' | 'subtle' | 'rounded' | 'soft' }; layout: { contentWidth: 'compact' | 'normal' | 'wide'; density: 'compact' | 'normal' | 'spacious'; pageWidth?: 'inset' | 'nav' }; motion: 'minimal' | 'normal' };
  header: { layout: 'standard' | 'centered' | 'split'; behavior: 'static' | 'sticky' | 'transparent' | 'transparent-sticky'; surface: 'light' | 'dark' | 'theme'; backgroundColor?: string | null; textColor?: string | null; stickyBackgroundColor?: string | null; stickyTextColor?: string | null; stickyShadow?: 'none' | 'subtle' | 'strong'; mobileMenuStyle?: 'dropdown' | 'offcanvas' | 'fullscreen'; mobileMenuButton?: 'lines' | 'dots' | 'label'; showSiteName: boolean; logoAssetId?: string | null; cta?: { label: string; pageId?: string | null; href?: string | null } | null };
  footer: SiteFooter;
  settings: { siteName: string; logoAssetId?: string | null; faviconAssetId?: string | null; defaultOgImageAssetId?: string | null };
}

export type ShadowLevel = 'none' | 'subtle' | 'medium' | 'strong';
export type BorderLevel = 'none' | 'subtle' | 'accent' | 'strong';
export type RadiusLevel = 'sharp' | 'subtle' | 'rounded' | 'soft' | 'pill';
export type HoverEffect = 'none' | 'lift' | 'shadow' | 'accent' | 'scale' | 'zoom';
/** Bounded entrance effects. The renderer owns the animation; content only
 * chooses a named, accessible preset. */
export type EntranceEffect = 'none' | 'fade' | 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right' | 'zoom' | 'blur' | 'rotate' | 'flip' | 'reveal' | 'assemble';
export type ElementIconPosition = 'top' | 'left' | 'right';
export type IconName =
  | 'none' | 'arrow-right' | 'arrow-down' | 'external' | 'mail' | 'phone' | 'download'
  | 'check' | 'star' | 'heart' | 'users' | 'calendar' | 'map-pin' | 'play' | 'chevron-right'
  | 'arrow-up-right' | 'chevron-down' | 'home' | 'church' | 'sparkles' | 'camera' | 'image'
  | 'music' | 'book-open' | 'hand-heart' | 'user' | 'message-circle' | 'clock' | 'gift'
  | 'globe' | 'search' | 'phone-call' | 'calendar-check' | 'briefcase' | 'graduation-cap'
  | 'shield-check' | 'heart-handshake' | 'leaf' | 'coffee' | 'utensils' | 'baby' | 'accessibility'
  | 'car' | 'info' | 'circle-help' | 'quote' | 'megaphone' | 'mic' | 'video' | 'file-text'
  | 'folder' | 'link' | 'lock' | 'map' | 'navigation' | 'landmark'
  /** Presentation-only choices for feature lists. */
  | 'dot' | 'arrow';

export interface SurfaceEffects {
  shadow?: ShadowLevel;
  border?: BorderLevel;
  radius?: RadiusLevel;
  hover?: HoverEffect;
}

export interface ResponsiveSectionHeight {
  desktop: number;
  tablet?: number;
  mobile?: number;
}

export interface VisualSurface {
  shape?: 'rectangle' | 'rounded' | 'ellipse' | 'blob' | 'line';
  fillMode?: 'theme' | 'custom' | 'gradient';
  fillColor?: ColorRole;
  customColor?: string | null;
  gradientFrom?: ColorRole;
  gradientFromCustom?: string | null;
  gradientTo?: ColorRole;
  gradientToCustom?: string | null;
  gradientDirection?: 'to-right' | 'to-bottom' | 'to-bottom-right' | 'radial';
  borderColor?: ColorRole;
  borderCustomColor?: string | null;
  borderWidth?: number;
  borderStyle?: 'none' | 'solid' | 'dashed';
  radius?: number;
  opacity?: number;
  shadow?: ShadowLevel;
  rotation?: number;
}

/**
 * Where a button goes. Mirrors `design_schemas.ButtonDestination`.
 *
 * `page`/`anchor` hold an id rather than a path, so an internal link survives
 * a slug rename. The unused fields are kept when switching kind, so toggling
 * between "Interne pagina" and "Externe link" does not discard what was
 * already typed.
 */
export interface ButtonDestination {
  kind: 'page' | 'anchor' | 'url' | 'email' | 'phone' | 'file';
  pageId?: string | null;
  anchor?: string | null;
  url?: string | null;
  email?: string | null;
  phone?: string | null;
  assetId?: string | null;
  newTab?: boolean;
}

export interface ButtonPresentation {
  style?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost' | 'text';
  /** Theme role for the button paint. `customColor` wins when present. */
  color?: ColorRole | null;
  /** One validated, intentional per-button paint override. */
  customColor?: string | null;
  size?: 'small' | 'normal' | 'large';
  width?: 'auto' | 'full';
  radius?: RadiusLevel | null;
  shadow?: ShadowLevel;
  hover?: HoverEffect;
  icon?: IconName;
  iconPosition?: 'left' | 'right';
}

export interface ContentBlock {
  type: BlockType;
  blockId: string;
  /** Mag leeg zijn: een net toegevoegd blok is nog geen inhoud. */
  text: string;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  label?: string;
  href?: string;
  assetId?: string;
  alt?: string;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Element-specific variant. The valid set depends on `type`
   * (button: primary|secondary|text, badge: plain|pill|accent,
   * gallery: grid|feature), which a single union cannot express without a
   * discriminated block type. Mirrors `builder_schemas.BuilderV2Block.variant`,
   * a bounded string the server validates per element.
   */
  variant?: string;
  caption?: string;
  organizationName?: string;
  address?: string;
  email?: string;
  phone?: string;
  role?: string;
  formId?: string;
  title?: string;
  description?: string;
  fields?: Array<{
    fieldId: string;
    label: string;
    type: 'text' | 'email' | 'textarea' | 'select' | 'checkbox';
    required?: boolean;
    placeholder?: string;
    options?: string[];
  }>;
  submitLabel?: string;
  successMessage?: string;
  /** Mirrors `builder_schemas.BuilderCardItem`. */
  items?: Array<{
    title: string;
    text: string;
    textFontSize?: number;
    href?: string;
    pageId?: string | null;
    assetId?: string | null;
    icon?: IconName;
    iconPosition?: ElementIconPosition;
  }>;
  features?: Array<{ text: string }>;
  questions?: Array<{ question: string; answer: string }>;
  assetIds?: string[];
  provider?: 'youtube' | 'vimeo' | 'asset';
  videoId?: string;
  url?: string;
  pageId?: string;
  fit?: 'cover' | 'contain';
  aspect?: 'original' | 'landscape' | 'square' | 'portrait' | 'wide';
  destination?: ButtonDestination | null;
  button?: ButtonPresentation | null;
  effects?: SurfaceEffects | null;
  motion?: EntranceEffect;
  motionDelay?: number;
  motionDuration?: number;
  shape?: VisualSurface;
  icon?: IconName | null;
  iconPosition?: ElementIconPosition;
  layout?: ElementLayout;
  typography?: ElementTypography;
  customClasses?: string[];
  html?: string;
  css?: string;
}

export interface SitePage {
  pageId: string;
  /** Lege string = homepage. Eén kebab-segment, geen geneste paden. */
  slug: string;
  title: string;
  description?: string | null;
  /** Legacy compatibility projection. Sections are the only authoring authority. */
  blocks: ContentBlock[];
  navigation?: {
    label: string;
    include: boolean;
    order: number;
    /** Optional parent page for a one-level site navigation dropdown. */
    parentPageId?: string | null;
  } | null;
  sections?: SiteSection[];
  seo?: { title: string; description: string; ogImageAssetId?: string | null };
  developerCss?: string;
}

export interface BuilderDraft {
  draftId: string;
  websiteId: string;
  /** Concurrency-token. Wordt onveranderd teruggestuurd bij elke opslag. */
  revision: number;
  pages: SitePage[];
  updatedAt: string;
  contentSchemaVersion?: 1 | 2 | 3;
  site?: SiteChrome;
}

export interface WebsiteSummary {
  websiteId: string;
  name: string;
  pageCount: number;
  isPublished: boolean;
  updatedAt?: string | null;
}

export interface WebsiteDetail {
  websiteId: string;
  name: string;
  /** Uit de domeinbindingen van Zoea. `null` = nog geen developmentadres. */
  developmentHostname: string | null;
  /** De Website Module-host die exact dezelfde renderer/CSS draait als Preview/Public. */
  editorCanvasUrl?: string | null;
  canPublish: boolean;
  isPublished: boolean;
  canDeveloper: boolean;
}

export interface DomainBinding {
  bindingId: string;
  websiteId: string;
  hostname: string;
  environment: 'development' | 'production';
  isPrimary: boolean;
}

export interface DeveloperDiagnostic {
  severity: 'error' | 'warning';
  message: string;
  line: number;
  column: number;
  field: 'html' | 'css';
}

export interface DeveloperDocument {
  websiteId: string;
  pageId: string;
  revision: number;
  html: string;
  css: string;
  pageCss: string;
  diagnostics: DeveloperDiagnostic[];
}

export interface DevelopmentSiteSession {
  grantId: string;
  activationUrl: string;
  hostname: string;
  expiresAt: string;
}

export type SaveStatus =
  | 'idle'
  | 'dirty'
  | 'saving'
  | 'saved'
  | 'error'
  | 'conflict';

export type ViewportKey = 'desktop' | 'tablet' | 'mobile';

export type FreeformHeightMode = 'auto' | 'fixed';

export interface FreeformGeometry {
  x: number;
  y: number;
  width: number;
  heightMode: FreeformHeightMode;
  height?: number | null;
  layer: 'background' | 'normal' | 'foreground';
}

export interface FreeformLayout {
  placementVersion: 2;
  desktop: FreeformGeometry;
  tablet?: FreeformGeometry;
  mobile?: FreeformGeometry;
}

/** Canonical visual placement. The desktop value is the base; narrower
 * viewports carry only intentional overrides. This is authoring data, not a
 * second CSS/layout language. */
export interface ElementLayout {
  desktop: GridPlacement;
  tablet?: Partial<GridPlacement>;
  mobile?: Partial<GridPlacement>;
}

export interface GridPlacement {
  gridStart: number;
  gridSpan: number;
  offsetX: number;
  offsetY: number;
  /** Optional continuous canvas width in logical pixels. */
  width?: number | null;
  height?: number | null;
  layer: 'background' | 'normal' | 'foreground';
}

export interface ElementTypography {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 300 | 400 | 500 | 600 | 700 | 800 | 900;
  lineHeight?: number;
  letterSpacing?: number;
  textAlign?: 'left' | 'center' | 'right';
  color?: ColorRole;
  customColor?: string | null;
}
